/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import { theme } from "@nordcloud/gnui";
import { ReservationUtilizationTimePeriod } from "~/generated/graphql";
import { dateFormat } from "~/constants/dateFormat";
import { generateNumericArray } from "~/tools";
import { Bars, ChartContainer } from "./components";
import { DataItem } from "./types";
import { getXScale, getYScale } from "./utils";

const VERTICAL_MARGIN = 40;

const barsColorTheme = {
  gray: [theme.colors.darks[4], theme.colors.darks[3]],
  normal: [theme.colors.success, theme.colors.danger],
} as const;

type Props<T> = {
  width: number;
  height: number;
  data: T[];
  loading?: boolean;
  period: ReservationUtilizationTimePeriod;
};

export function MonthlyUtilisationChart<T extends DataItem>({
  width,
  height,
  data = [],
  loading = true,
  period,
}: Props<T>) {
  const xMax = width - 30;
  const yMax = height - VERTICAL_MARGIN;
  const BARS_NUMBER = dayjs().daysInMonth();
  const numTicks = data.length;
  const utilisationData = new Array(numTicks).fill(0);
  const benefitData = new Array(numTicks).fill(0);
  const xScale = getXScale(xMax, utilisationData);
  const yScale = getYScale(yMax);
  const barWidth = Number(xMax) / (numTicks * 1.5);

  data.forEach((item, index) => {
    utilisationData.splice(index, 1, Number(item?.value) * 100 || 0);
    benefitData.splice(index, 1, Number(item?.benefit) || 0);
  });

  const ticks = React.useMemo(() => {
    if (period === ReservationUtilizationTimePeriod.OneMonth) {
      const startDayOfMonth = dayjs(data?.[0]?.date);
      const dates = generateNumericArray(BARS_NUMBER);
      return dates.map((dayNumber) => {
        return startDayOfMonth
          .add(dayNumber, "day")
          .format(dateFormat.dayMonthShort);
      });
    }

    return data.map((dt, index) => {
      if (period === ReservationUtilizationTimePeriod.ThreeMonths) {
        return index % 7 === 0
          ? dayjs(dt.date).format(dateFormat.dayMonthShort)
          : "";
      }

      return dayjs(dt.date).format(dateFormat.monthYearShort);
    });
  }, [data, period, BARS_NUMBER]);

  return (
    <ChartContainer
      data={data}
      loading={loading}
      width={width}
      height={height}
      yTicks={10}
      numTicks={numTicks}
      offset={30}
      xTickFormat={(number: number) => ticks[number]}
    >
      <Bars
        data={utilisationData}
        yScale={yScale}
        xScale={xScale}
        yMax={yMax}
        benefit={benefitData}
        barWidth={barWidth}
      />
    </ChartContainer>
  );
}

export const legend = [
  {
    color: barsColorTheme.normal[1],
    opacity: 0.6,
    label: "underutilized",
    hasTooltip: true,
    caption: "Total cost on-demand equivalent - total reservation cost < 0",
  },
  {
    color: barsColorTheme.normal[0],
    opacity: 0.6,
    label: "utilized",
    hasTooltip: true,
    caption: "Total cost on-demand equivalent - total reservation cost >= 0",
  },
];
